/**
 * Matches — full fixture browser (brief §5 secondary surface).
 *
 * Pulls every match in the next ~14 days from /api/v1/matches and
 * groups them by date. Tap a row to open Match detail. If an edge
 * call exists for the fixture, it'll be shown alongside (later).
 *
 * Source: Postgres `matches` table, populated by the cron's
 * api-football ingestion. Falls back to stub matches if the cron
 * hasn't run yet.
 */

import { useEffect, useState } from 'react';

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api/v1';

interface MatchRow {
  id: string;
  league: { id: string; name: string; shortName: string; badgeUrl: string };
  home:   { id: string; shortName: string; crestUrl: string; primaryColor: string };
  away:   { id: string; shortName: string; crestUrl: string; primaryColor: string };
  kickoff: string;
  state: 'pre' | 'live' | 'ft' | 'settled';
  score: { home: number; away: number; minute: string | null } | null;
}

interface Props {
  onOpenMatch(matchId: string): void;
}

async function fetchMatches(): Promise<MatchRow[]> {
  const res = await fetch(`${API_BASE.replace(/\/$/, '')}/matches?fromDays=-1&toDays=14&limit=300`);
  if (!res.ok) throw new Error(`/matches ${res.status}`);
  const j = await res.json() as { matches: MatchRow[] };
  return j.matches;
}

function dateLabel(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const tomorrow = new Date(); tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);

  const isSameDay = (a: Date, b: Date) =>
    a.getUTCFullYear() === b.getUTCFullYear()
    && a.getUTCMonth() === b.getUTCMonth()
    && a.getUTCDate() === b.getUTCDate();

  if (isSameDay(d, today)) return 'Today';
  if (isSameDay(d, tomorrow)) return 'Tomorrow';
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

function timeLabel(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function groupByDate(rows: MatchRow[]): Map<string, MatchRow[]> {
  const m = new Map<string, MatchRow[]>();
  for (const r of rows) {
    const key = r.kickoff.slice(0, 10);
    if (!m.has(key)) m.set(key, []);
    m.get(key)!.push(r);
  }
  return m;
}

export function Matches({ onOpenMatch }: Props) {
  const [rows, setRows] = useState<MatchRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchMatches()
      .then((m) => { if (!cancelled) setRows(m); })
      .catch((e) => { if (!cancelled) setError(String(e?.message ?? e)); });
    return () => { cancelled = true; };
  }, []);

  if (error) {
    return <div className="matches-loading">Couldn't load fixtures: {error}</div>;
  }
  if (rows === null) {
    return <div className="matches-loading">Loading fixtures…</div>;
  }
  if (rows.length === 0) {
    return (
      <div className="matches-empty">
        <div className="matches-empty__title">No fixtures yet</div>
        <div className="matches-empty__body">
          The cron service ingests fixtures from api-football every 6 hours.
          New fixtures should appear within the next ingestion cycle.
        </div>
      </div>
    );
  }

  const grouped = groupByDate(rows);

  return (
    <div className="matches">
      <section className="matches__head">
        <div className="matches__eyebrow">Sharp8 · Matches</div>
        <h1 className="matches__title">Today and ahead</h1>
        <p className="matches__sub">
          EPL · La Liga · AFC CL · J-League · K-League — fixtures via api-football.
        </p>
      </section>

      {Array.from(grouped.entries()).map(([dateKey, items]) => (
        <section key={dateKey} className="matches__group">
          <div className="matches__group-head">{dateLabel(items[0]!.kickoff)}</div>
          <ul className="matches__list">
            {items.map((m) => (
              <li key={m.id}>
                <button className="matches__row" onClick={() => onOpenMatch(m.id)}>
                  <span className="matches__time">
                    {m.state === 'live' && (m.score?.minute ?? "LIVE")}
                    {m.state === 'ft' && 'FT'}
                    {m.state === 'pre' && timeLabel(m.kickoff)}
                  </span>

                  <span className="matches__league" title={m.league.name}>{m.league.shortName}</span>

                  <span className="matches__teams">
                    <span className="matches__side">
                      <Crest url={m.home.crestUrl} color={m.home.primaryColor} initial={m.home.shortName[0] ?? '?'} />
                      <span className="matches__teamname">{m.home.shortName}</span>
                    </span>
                    <span className="matches__score">
                      {m.score ? `${m.score.home}–${m.score.away}` : <span className="matches__vs">vs</span>}
                    </span>
                    <span className="matches__side matches__side--right">
                      <span className="matches__teamname">{m.away.shortName}</span>
                      <Crest url={m.away.crestUrl} color={m.away.primaryColor} initial={m.away.shortName[0] ?? '?'} />
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function Crest({ url, color, initial }: { url: string; color: string; initial: string }) {
  return (
    <span className="matches__crest" style={{ background: 'var(--surface-tint)' }}>
      {url ? (
        <img
          src={url}
          alt=""
          onError={(e) => {
            const img = e.currentTarget;
            const parent = img.parentElement;
            if (parent) {
              parent.style.background = color;
              parent.innerHTML = `<span style="color:#fff;font-weight:800;font-size:11px">${initial}</span>`;
            }
          }}
        />
      ) : (
        <span style={{ color: '#fff', fontWeight: 800, fontSize: 11, background: color, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {initial}
        </span>
      )}
    </span>
  );
}
